import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  FileText,
  Download,
  Loader,
  ChevronDown,
  File,
  Code,
  FileType,
  X
} from 'lucide-react';
import { TextReportGenerator } from '../../utils/textReportGenerator';
import { MarkdownReportGenerator } from '../../utils/MarkdownReportGenerator';
import { EnhancedHtmlReportGenerator } from '../../utils/enhancedHtmlReportGenerator';
import ReportContentGenerator from '../../utils/reportContentGenerator';
import MatrixReportGenerator from '../../utils/matrixReportGenerator';
import { generateFilenames } from '../../utils/filenameGenerator';
import apiClient from '../../api/apiClient';
import reportService from '../../services/reportService';

/**
 * Unified Report Button Component
 * Combines HTML, Text, and Markdown report generation into one button with format selection
 */
const UnifiedReportButton = ({
  analysisId,
  reportData,
  kpiData,
  chartData,
  logoData,
  filename = 'rapport-ebios-rm',
  buttonText = 'Exporter le Rapport',
  className = '',
  variant = 'primary',
  size = 'default'
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [showFormatSelector, setShowFormatSelector] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState('pdf-matrix');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);

  const reportFormats = [
    // Temporarily commented out - HTML report format
    /*
    {
      id: 'html',
      name: 'Rapport HTML',
      description: 'Rapport enrichi avec graphiques et mise en page professionnelle',
      icon: FileText,
      extension: 'html',
      mimeType: 'text/html;charset=utf-8'
    },
    */
    /*
    {
      id: 'pdf-fr',
      name: '🇫🇷 Rapport PDF (Français)',
      description: 'Rapport professionnel au format PDF en français',
      icon: FileType,
      extension: 'pdf',
      mimeType: 'application/pdf',
      language: 'fr'
    },
    {
      id: 'pdf-en',
      name: '🇬🇧 PDF Report (English)',
      description: 'Professional PDF report in English',
      icon: FileType,
      extension: 'pdf',
      mimeType: 'application/pdf',
      language: 'en'
    },
    */
    {
      id: 'pdf-matrix',
      name: 'PTR FR',
      description: 'Livrable officiel avec structure matricielle et vocabulaire EBIOS RM (Français)',
      icon: FileText,
      extension: 'pdf',
      mimeType: 'application/pdf',
      language: 'fr'
    },
    {
      id: 'pdf-matrix-en',
      name: 'PTR EN',
      description: 'Official deliverable with matrix structure and EBIOS RM terminology (English)',
      icon: FileText,
      extension: 'pdf',
      mimeType: 'application/pdf',
      language: 'en'
    },
    {
      id: 'docx-matrix',
      name: 'PTR DOCX FR',
      description: 'Rapport complet au format Word (.docx) modifiable — Français',
      icon: FileText,
      extension: 'docx',
      mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      language: 'fr'
    },
    {
      id: 'docx-matrix-en',
      name: 'PTR DOCX EN',
      description: 'Complete report as editable Word (.docx) — English',
      icon: FileText,
      extension: 'docx',
      mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      language: 'en'
    },
    // Temporarily commented out - Text report format
    /*
    {
      id: 'txt',
      name: 'Rapport Texte',
      description: 'Rapport au format texte brut pour analyse détaillée',
      icon: File,
      extension: 'txt',
      mimeType: 'text/plain;charset=utf-8'
    },
    */
    // Temporarily commented out - Markdown report format
    /*
    {
      id: 'md',
      name: 'Rapport Markdown',
      description: 'Rapport au format Markdown pour documentation',
      icon: Code,
      extension: 'md',
      mimeType: 'text/markdown;charset=utf-8'
    }
    */
  ];

  const handleGenerateReport = async (format) => {
    console.log(`🚀 [UNIFIED REPORT] Starting report generation for format: ${format}`);
    setShowFormatSelector(false);
    setIsGenerating(true);
    setError(null);

    try {
      let dataToUse = reportData;

      // Fetch data from backend if not provided
      if (!dataToUse && analysisId) {
        console.log(`📡 [${format.toUpperCase()} REPORT] Fetching comprehensive workshop data from backend...`);
        const response = await apiClient.get(`/analyses/${analysisId}/text-report-data`);

        if (!response.success) {
          throw new Error('Failed to fetch workshop data from backend');
        }

        dataToUse = response.data;
        console.log(`✅ [${format.toUpperCase()} REPORT] Workshop data fetched successfully`);
      }

      if (!dataToUse) {
        setError('Aucune donnée de rapport disponible');
        return;
      }

      let reportContent;
      let mimeType;
      let finalFilename;

      // Extract metadata for filename generation
      const metadata = dataToUse?.metadata || {};
      console.log('📋 [FILENAME] Using metadata for filename:', metadata);

      switch (format) {
        // Temporarily commented out - HTML report generation
        /*
        case 'html':
          // Generate HTML report
          const htmlGenerator = new EnhancedHtmlReportGenerator(dataToUse, kpiData, chartData, logoData, { analysisId: analysisId });
          reportContent = await htmlGenerator.generateCompleteReport();
          mimeType = 'text/html;charset=utf-8';
          finalFilename = generateFilenames.htmlReport(metadata);
          break;
        */

        case 'pdf-fr':
        case 'pdf-en':
          // Generate PDF report (French or English)
          const isEnglish = format === 'pdf-en';
          console.log(`📄 [${format.toUpperCase()} REPORT] Starting PDF generation for ${filename} (${isEnglish ? 'English' : 'French'})`);
          console.log('📊 PDF Data:', { dataToUse: !!dataToUse, kpiData: !!kpiData, chartData: !!chartData, logoData: !!logoData });

          // Fetch data from backend if not provided
          if (!dataToUse && analysisId) {
            console.log(`📡 [${format.toUpperCase()} REPORT] Fetching comprehensive workshop data from backend...`);
            const response = await apiClient.get(`/analyses/${analysisId}/text-report-data`);

            if (!response.success) {
              throw new Error('Failed to fetch workshop data from backend');
            }

            dataToUse = response.data;
            console.log(`✅ [${format.toUpperCase()} REPORT] Workshop data fetched successfully`);
          }

          // Use bilingual base generator; trailing lang param selects output language
          const pdfGenerator = new ReportContentGenerator(
            dataToUse,
            kpiData,
            chartData,
            null,
            logoData,
            { workshop5Only: false, analysisId: analysisId },
            isEnglish ? 'en' : 'fr'
          );

          reportContent = await pdfGenerator.generateCompleteReport();

          console.log(`✅ [${format.toUpperCase()} REPORT] PDF generated successfully, size: ${reportContent?.size || 'unknown'} bytes`);
          mimeType = 'application/pdf';
          const langSuffix = isEnglish ? '-EN' : '-FR';
          finalFilename = `${generateFilenames.pdfReport(metadata).replace('.pdf', '')}${langSuffix}.pdf`;
          break;

        case 'pdf-matrix':
        case 'pdf-matrix-en':
          const isMatrixEnglish = format === 'pdf-matrix-en';
          // Generate the new specialized Matrix Report (French or English)
          console.log(`📑 [MATRIX REPORT] Starting professional Matrix PDF generation for ${filename} (${isMatrixEnglish ? 'English' : 'French'})`);

          // Fetch base report data
          if (!dataToUse && analysisId) {
            const response = await apiClient.get(`/analyses/${analysisId}/text-report-data`);
            if (!response.success) throw new Error('Failed to fetch data for matrix report');
            dataToUse = response.data;
          }

          // CRITICAL: Fetch correct Workshop 5 data from dedicated endpoint
          // to avoid riskScore being recalculated incorrectly in text-report-data
          if (analysisId) {
            try {
              console.log(`📡 [MATRIX REPORT] Fetching /report-section5 for correct Workshop 5 data...`);
              const section5Response = await apiClient.get(`/analyses/${analysisId}/report-section5`);
              if (section5Response.success && section5Response.data?.strategicScenarios) {
                console.log(`✅ [MATRIX REPORT] /report-section5 returned ${section5Response.data.strategicScenarios.length} scenarios`);
                const firstScenario = section5Response.data.strategicScenarios[0];
                if (firstScenario) {
                  console.log(`[MATRIX REPORT] /report-section5 first scenario: id=${firstScenario.id}, riskScore=${firstScenario.riskScore}, gravity=${firstScenario.gravity}, likelihood=${firstScenario.likelihood}`);
                }
                if (!dataToUse.workshops) dataToUse.workshops = {};
                dataToUse.workshops.workshop5 = {
                  ...dataToUse.workshops?.workshop5,
                  strategicScenarios: section5Response.data.strategicScenarios,
                  _source: 'report-section5-endpoint',
                };
              }
            } catch (section5Err) {
              console.warn(`⚠️ [MATRIX REPORT] /report-section5 fetch failed, falling back to text-report-data workshop5:`, section5Err.message);
            }
          }

          const matrixGenerator = new MatrixReportGenerator(dataToUse, analysisId, isMatrixEnglish ? 'en' : 'fr');
          reportContent = await matrixGenerator.generateMatrixReport();

          console.log(`✅ [MATRIX REPORT] Professional PDF generated successfully`);
          mimeType = 'application/pdf';
          const matrixLangSuffix = isMatrixEnglish ? '_EN' : '_FR';
          finalFilename = `Rapport_Matrice_${metadata.companyName || 'EBIOS'}_${new Date().toISOString().split('T')[0]}${matrixLangSuffix}.pdf`;
          break;

        // Temporarily commented out - Other report formats
        /*
        case 'pdf-workshop5':
          // Generate Workshop 5 only PDF report
          console.log(`📄 [WORKSHOP5 REPORT] Starting PDF generation for ${filename}`);
          console.log('📊 PDF Data:', { dataToUse: !!dataToUse, kpiData: !!kpiData, chartData: !!chartData, logoData: !!logoData });

          // Fetch data from backend if not provided
          if (!dataToUse && analysisId) {
            console.log(`📡 [WORKSHOP5 REPORT] Fetching comprehensive workshop data from backend...`);
            const response = await apiClient.get(`/analyses/${analysisId}/text-report-data`);

            if (!response.success) {
              throw new Error('Failed to fetch workshop data from backend');
            }

            dataToUse = response.data;
            console.log(`✅ [WORKSHOP5 REPORT] Workshop data fetched successfully`);
          }

          const workshop5Generator = new ReportContentGenerator(
            dataToUse,
            kpiData,
            chartData,
            null,
            logoData,
            { workshop5Only: true, analysisId: analysisId }
          );

          reportContent = await workshop5Generator.generateWorkshop5Report();

          console.log(`✅ [WORKSHOP5 REPORT] PDF generated successfully, size: ${reportContent?.size || 'unknown'} bytes`);
          mimeType = 'application/pdf';
          finalFilename = generateFilenames.workshop5Report(metadata);
          break;

        case 'txt':
          // Generate Text report
          const textGenerator = new TextReportGenerator(dataToUse, kpiData, chartData, logoData);
          reportContent = await textGenerator.generateCompleteTextReport();
          mimeType = 'text/plain;charset=utf-8';
          finalFilename = generateFilenames.textReport(metadata);
          break;

        case 'md':
          // Generate Markdown report
          const mdGenerator = new MarkdownReportGenerator(dataToUse);
          reportContent = await mdGenerator.generateCompleteMarkdownReport();
          mimeType = 'text/markdown;charset=utf-8';
          finalFilename = generateFilenames.markdownReport(metadata);
          break;
        */

        case 'docx-matrix':
        case 'docx-matrix-en': {
          const isDocxEn = format === 'docx-matrix-en';
          // Generate the DOCX complete report (handles its own download)
          console.log(`📑 [DOCX REPORT] Starting complete DOCX generation (${isDocxEn ? 'EN' : 'FR'})`);

          if (!dataToUse && analysisId) {
            const response = await apiClient.get(`/analyses/${analysisId}/text-report-data`);
            if (!response.success) throw new Error('Failed to fetch data for DOCX report');
            dataToUse = response.data;
          }

          // Fetch correct Workshop 5 data from dedicated endpoint
          // to match the risk scores shown in the matrix PDF report
          if (analysisId) {
            try {
              const section5Response = await apiClient.get(`/analyses/${analysisId}/report-section5`);
              if (section5Response.success && section5Response.data?.strategicScenarios) {
                if (!dataToUse.workshops) dataToUse.workshops = {};
                dataToUse.workshops.workshop5 = {
                  ...dataToUse.workshops?.workshop5,
                  strategicScenarios: section5Response.data.strategicScenarios,
                  _source: 'report-section5-endpoint',
                };
              }
            } catch (section5Err) {
              console.warn(`⚠️ [DOCX REPORT] /report-section5 fetch failed, falling back:`, section5Err.message);
            }
          }

          const { generateCompleteDocxReport } = await import('../../utils/completeDocxReportGenerator');
          await generateCompleteDocxReport(dataToUse, analysisId, isDocxEn ? 'en' : 'fr');

          console.log(`✅ [DOCX REPORT] Complete DOCX generated successfully (${isDocxEn ? 'EN' : 'FR'})`);
          // Skip blob download — the generator handles it internally
          setIsGenerating(false);
          return;
        }

        default:
          throw new Error('Format de rapport non supporté');
      }

      // Create and download the file
      const blob = new Blob([reportContent], { type: mimeType });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = finalFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);

      console.log(`✅ Rapport ${format.toUpperCase()} généré et téléchargé avec succès`);

    } catch (err) {
      console.error(`❌ Erreur lors de la génération du rapport ${format}:`, err);
      setError(`Erreur lors de la génération du rapport: ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const selectedFormatInfo = reportFormats.find(f => f.id === selectedFormat);
  const SelectedIcon = selectedFormatInfo?.icon || FileText;

  // Button size classes
  const sizeClasses = {
    small: 'px-3 py-1.5 text-sm',
    default: 'px-4 py-2 text-base',
    large: 'px-6 py-3 text-lg'
  };

  // Button variant classes
  const variantClasses = {
    primary: 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600',
    secondary: 'bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300',
    success: 'bg-green-600 hover:bg-green-700 text-white border-green-600',
    outline: 'bg-transparent hover:bg-blue-50 text-blue-600 border-blue-600'
  };

  // Calculate dropdown position when it opens
  useEffect(() => {
    if (showFormatSelector && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 200; // Approximate height of dropdown
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;

      let top;
      if (spaceBelow >= dropdownHeight || spaceBelow > spaceAbove) {
        // Show below if there's enough space
        top = rect.bottom + window.scrollY + 7;
      } else {
        // Show above if there's more space above
        top = rect.top + window.scrollY - dropdownHeight - 10;
      }

      setDropdownPosition({
        top: Math.max(4, top), // Ensure it doesn't go off-screen
        left: rect.left + window.scrollX + -3,
        width: rect.width
      });
    }
  }, [showFormatSelector]);

  const buttonClasses = `
    inline-flex items-center justify-center
    border rounded-lg font-medium
    transition-all duration-200
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
    disabled:opacity-50 disabled:cursor-not-allowed
    ${sizeClasses[size]}
    ${variantClasses[variant]}
    ${className}
  `.trim();

  return (
    <div className={`relative inline-block ${className}`} ref={buttonRef}>
      {/* Main Button */}
      <div className="flex">
        <button
          onClick={() => handleGenerateReport(selectedFormat)}
          disabled={isGenerating}
          className={`${buttonClasses} rounded-r-none border-r-0`}
          title={`Générer un rapport ${selectedFormatInfo?.name || 'EBIOS RM'}`}
        >
          {isGenerating ? (
            <>
              <Loader className="w-4 h-4 mr-2 animate-spin" />
              Génération...
            </>
          ) : (
            <>
              <SelectedIcon className="w-4 h-4 mr-2" />
              {buttonText}
            </>
          )}
        </button>

        {/* Format Selector Button */}
        <button
          onClick={() => setShowFormatSelector(!showFormatSelector)}
          disabled={isGenerating}
          className={`${buttonClasses} rounded-l-none border-l-0 px-2`}
          title="Choisir le format d'export"
        >
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>

      {/* Format Selector Dropdown - Rendered via Portal */}
      {showFormatSelector && ReactDOM.createPortal(
        <div
          className="absolute bg-white border border-gray-200 rounded-lg shadow-lg z-50"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${Math.max(dropdownPosition.width, 320)}px`,
            minWidth: '320px',
            maxWidth: '400px'
          }}
        >
          <div className="p-2">
            <div className="text-xs font-medium text-gray-500 mb-2 px-2">Format d'export:</div>
            {reportFormats.map((format) => {
              const FormatIcon = format.icon;
              return (
                <button
                  key={format.id}
                  onClick={() => {
                    setSelectedFormat(format.id);
                    setShowFormatSelector(false);
                  }}
                  className={`w-full flex items-center space-x-3 px-3 py-2 text-left rounded-md transition-colors ${selectedFormat === format.id
                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                    : 'hover:bg-gray-50 text-gray-700'
                    }`}
                >
                  <FormatIcon className="w-4 h-4 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{format.name}</div>
                    <div className="text-xs text-gray-500">{format.description}</div>
                  </div>
                  {selectedFormat === format.id && (
                    <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></div>
                  )}
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}

      {/* Error Display */}
      {error && (
        <div className="mt-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
          <div className="flex items-start space-x-2">
            <X className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium">Erreur de génération</div>
              <div className="mt-1">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close dropdown */}
      {showFormatSelector && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowFormatSelector(false)}
        />
      )}
    </div>
  );
};

export default UnifiedReportButton;
